import { useSocket, useVoice } from "@next-platform/client-common-core";
import { SOCKET_SERVER } from "@next-platform/client-common-core/infra/constants";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { useEffect, useState } from "react";

import getUserPlan from "@/features/user/utils/getUserPlan";
import type { Conversation } from "@/types";

import { PREFERRED_VOICE_ACTIONS } from "../components/voices/constant";
import { ERROR_MESSAGES, IhandlePreferredVoiceActions, IUsePreferreedVoiceProps, SUCCESS_MESSAGES } from "../components/voices/types";
import useTextToSpeech from "./useTextToSpeech";

const logger = appLogger.withTag("usePreferredVoice");
const INFO_MESSAGE_TIMEOUT = 5000;

export default function usePreferredVoice({
  voiceId,
  setVoiceId,
  conversationId,
  handleLoadedMessages,
  displaySuccess,
  displayError,
  setSelectedMessageToPlay,
}: IUsePreferreedVoiceProps) {
  const {
    getVoices,
    setConversationVoiceId: saveConversationVoiceId,
    rateVoiceQuality,
    createConversationAndSetVoiceId,
    loading: loadingFromPreferredVoice,
  } = useVoice();
  const [errorEvent, setErrorEvent] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSocketError = (errorEvent: string, response?: string) => {
    setErrorEvent(errorEvent);
    setErrorMessage(response || "");
    stopAudioPlay();
  };

  const resetSocketErrors = () => {
    setErrorEvent(null);
    setErrorMessage(null);
  };

  const { stopAudioPlay, playChatMessage, setMessageId: setAudioPlayingMessageId, currentAudioMessageId, isAudioLoading } = useTextToSpeech();

  const setMessageId = (messageId: string) => {
    setAudioPlayingMessageId(messageId);
    return Promise.resolve();
  };

  const { requestAudioPlay, disconnectSocket } = useSocket({
    socketUrl: SOCKET_SERVER!,
    playChatMessage,
    setMessageId,
    messageId: currentAudioMessageId,
    handleError: handleSocketError,
  });

  const [rateVoiceModal, setRateVoiceModal] = useState({
    isVisible: false,
    messageId: "",
    voiceId: "",
  });
  const [infoMessage, setInfoMessage] = useState({
    messageId: "",
    message: "",
  });
  const { isUserPremium } = getUserPlan();

  useEffect(() => {
    return () => {
      // Clean up when the component is unmounted
      disconnectSocket();
      stopAudioPlay();
    };
  }, []);

  const handleRateVoiceQualityModalOpen = async (messageId: string) => {
    setRateVoiceModal({
      ...rateVoiceModal,
      ...{ messageId, voiceId, isVisible: true },
    });
  };

  const handleRateVoiceQualityModalClose = () => {
    setRateVoiceModal({ ...rateVoiceModal, isVisible: false });
  };

  const setInfoMessageForRatingVoice = (messageId: string, message: string) => {
    if (!messageId || !message) {
      return;
    }
    setInfoMessage({ messageId, message });
    setTimeout(() => setInfoMessage({ messageId: "", message: "" }), INFO_MESSAGE_TIMEOUT);
  };

  const handleAudioPlay = (messageId: string) => {
    if (!isUserPremium || !voiceId) {
      return;
    }
    if (messageId && voiceId) {
      requestAudioPlay(messageId, voiceId);
    }
  };

  const setConversationVoiceId = async (
    selectedVoiceId: string,
    selectedVoiceName: string,
    characterId: string,
    defaultPersonaId: string,
    errorCB: () => void,
    successCB: () => void,
  ) => {
    if (!conversationId) {
      try {
        const response = await createConversationAndSetVoiceId(characterId, defaultPersonaId, selectedVoiceId);
        if (response) {
          setSelectedMessageToPlay(response?.messages?.[0]?.id); // set initial greetings to play
          handleLoadedMessages({ res: response as unknown as Conversation });
          const successMessage = `${SUCCESS_MESSAGES.SAVE_CONVERSATION_VOICE_ID} ${selectedVoiceName}`;
          displaySuccess(successMessage);
          successCB?.();
        }
      } catch (error) {
        logger.error("Failed to set conversation voice id", error);
        errorCB?.();
        displayError(ERROR_MESSAGES.SAVE_CONVERSATION_VOICE_ID);
      }
      return;
    }
    handleSaveConversationVoiceId(selectedVoiceId, selectedVoiceName, errorCB, successCB);
  };

  const handleGetVoices = async (errorCB: () => void, successCB: () => void) => {
    try {
      await getVoices();
      successCB?.();
    } catch {
      displayError(ERROR_MESSAGES.GET_ALL_VOICES);
      errorCB?.();
    }
  };

  const handleSaveConversationVoiceId = async (selectedVoiceId: string, selectedVoiceName: string, errorCB: () => void, successCB: () => void) => {
    try {
      await saveConversationVoiceId(conversationId, selectedVoiceId);
      const successMessage = `${SUCCESS_MESSAGES.SAVE_CONVERSATION_VOICE_ID} ${selectedVoiceName || "Default Voice"}`;
      displaySuccess(successMessage);
      successCB?.();
      setVoiceId(selectedVoiceId);
    } catch (error) {
      logger.error("Failed to save conversation voice id", error);
      displayError(ERROR_MESSAGES.SAVE_CONVERSATION_VOICE_ID);
      errorCB?.();
    }
  };

  const handlePreferredVoiceActions = async (args: Partial<IhandlePreferredVoiceActions>) => {
    const { action } = args;
    if (!action) {
      return;
    }

    const { messageId = "", selectedVoiceId = "", selectedVoiceName = "", errorCB, successCB, voiceId } = args;
    const characterId = args.characterId ?? "";
    const defaultPersonaId = args.defaultPersonaId ?? "";

    switch (action) {
      case PREFERRED_VOICE_ACTIONS.Play:
        return handleAudioPlay(messageId);
      case PREFERRED_VOICE_ACTIONS.Stop:
        return stopAudioPlay();
      case PREFERRED_VOICE_ACTIONS.Rate_Modal:
        return handleRateVoiceQualityModalOpen(messageId);
      case PREFERRED_VOICE_ACTIONS.Set_Conversation_Voice_Id:
        return await setConversationVoiceId(selectedVoiceId, selectedVoiceName, characterId, defaultPersonaId, errorCB!, successCB!);
      case PREFERRED_VOICE_ACTIONS.Get_Voices:
        return await handleGetVoices(errorCB!, successCB!);
      case PREFERRED_VOICE_ACTIONS.Set_VoiceId:
        return setVoiceId(voiceId!);
    }
  };

  return {
    handlePreferredVoiceActions,
    stopAudioPlay,
    rateVoiceQuality,
    currentAudioMessageId,
    isAudioLoading,
    rateVoiceModal,
    handleRateVoiceQualityModalClose,
    setInfoMessageForRatingVoice,
    infoMessage,
    loadingFromPreferredVoice,
    errorEvent,
    errorMessage,
    resetSocketErrors,
  };
}
